import { useEffect, useMemo, useState } from "react";
import { NavLink } from "react-router-dom";
import api from "../../services/api";
import useProtectedFileUrl from "../../hooks/useProtectedFileUrl";
import { usePlatformConfig } from "../../context/PlatformConfigContext";
import { filterNavItemsByFeatures } from "../../utils/featureNav";
import { getProfilePathForRole } from "../../utils/profileMenuPaths";
import { useAdminPermissions } from "../../hooks/useAdminPermissions";
import { formatCaregiverLevel } from "../../utils/displayLabels";
import { isPhysioFromProfile, isPhysioServiceRole } from "../../utils/isPhysioCaregiver";
import { isNurseFromProfile, isNurseServiceRole } from "../../utils/isNurseCaregiver";
import { PHYSIO_SIDEBAR_ITEMS, calculatePhysioDossierPercent, mergePhysioDossier } from "../../config/physioCareHubConfig";
import { NURSE_SIDEBAR_ITEMS } from "../../config/nurseCareHubConfig";
import HeaderProfileMenu from "./HeaderProfileMenu";
import { ROLE_SIDEBAR_CONFIG } from "./roleSidebarConfig";
import {
  CARE_MANAGER_SIDEBAR_ITEMS,
  filterCareManagerSidebarItems,
} from "../caremanager/careManagerConfig";
import useCareManagerOnboardingStatus from "../../hooks/useCareManagerOnboardingStatus";
import { isCareManagerWorkspaceUnlocked } from "../../utils/careManagerAirlock";
import nemaCareLogo from "../../assets/design/nema-care-logo.png";

// ROLE_SIDEBAR_CONFIG is mutated when caregiver profile is saved so sidebar label stays in sync.
import "./RoleSidebar.css";

function Icon({ name }) {
  switch (name) {
    case "grid":
      return <svg className="role-side__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75"><rect x="3" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="3" width="7" height="7" rx="1.5" /><rect x="3" y="14" width="7" height="7" rx="1.5" /><rect x="14" y="14" width="7" height="7" rx="1.5" /></svg>;
    case "shield":
      return <svg className="role-side__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75"><path d="M12 3l8 4v6c0 5-3.5 9-8 10-4.5-1-8-5-8-10V7l8-4z" /></svg>;
    case "map":
      return <svg className="role-side__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75"><path d="M9 20l-6 2V6l6-2 6 2 6-2v14l-6 2-6-2z" /><path d="M9 4v16M15 6v14" /></svg>;
    case "chart":
      return <svg className="role-side__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75"><path d="M4 19V5M8 19V11M12 19V8M16 19v-6M20 19V9" /></svg>;
    case "alert":
      return <svg className="role-side__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75"><path d="M12 9v4M12 17h.01M10.3 3.3L2.4 18c-.4.7.1 1.6.9 1.6h17.4c.8 0 1.3-.9.9-1.6L13.7 3.3c-.4-.6-1.4-.6-1.8 0z" /></svg>;
    case "check":
      return (
        <svg
          className="role-side__icon"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.75"
        >
          <path d="M20 6L9 17l-5-5" />
        </svg>
      );
    case "list":
      return <svg className="role-side__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75"><path d="M8 6h12M8 12h12M8 18h12" /><circle cx="4" cy="6" r="1" /><circle cx="4" cy="12" r="1" /><circle cx="4" cy="18" r="1" /></svg>;
    case "home":
      return <svg className="role-side__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75"><path d="M3 11.5L12 4l9 7.5" /><path d="M5 10v10h14V10" /></svg>;
    case "search":
      return <svg className="role-side__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75"><circle cx="11" cy="11" r="6" /><path d="M20 20l-4.2-4.2" /></svg>;
    case "clock":
      return <svg className="role-side__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75"><circle cx="12" cy="12" r="8" /><path d="M12 8v4l3 2" /></svg>;
    case "calendar":
      return <svg className="role-side__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75"><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M8 3v4M16 3v4M3 10h18" /></svg>;
    case "document":
      return <svg className="role-side__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75"><path d="M7 3h7l5 5v13a1 1 0 01-1 1H7a1 1 0 01-1-1V4a1 1 0 011-1z" /><path d="M14 3v5h5M9 13h6M9 17h6" /></svg>;
    case "wallet":
      return <svg className="role-side__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75"><rect x="3" y="6" width="18" height="13" rx="2" /><path d="M3 10h18M16 14.5h2" /></svg>;
    case "help":
      return <svg className="role-side__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75"><circle cx="12" cy="12" r="9" /><path d="M9.5 9.5a2.5 2.5 0 015 0c0 1.5-2.5 2-2.5 3.5M12 17h.01" /></svg>;
    case "user":
      return <svg className="role-side__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75"><circle cx="12" cy="8" r="4" /><path d="M4 20c1.8-4.2 5-6 8-6s6.2 1.8 8 6" /></svg>;
    case "users":
      return <svg className="role-side__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75"><circle cx="9" cy="8" r="3.2" /><path d="M3.5 19c1.4-3.2 3.8-4.6 5.5-4.6s4.1 1.4 5.5 4.6" /><circle cx="16.5" cy="8.5" r="2.6" /><path d="M15.2 14.6c1.7 0 3.8 1.2 5.3 4.4" /></svg>;
    case "message":
      return <svg className="role-side__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75"><path d="M4 5h16v11H7l-3 3V5z" /></svg>;
    case "card":
      return <svg className="role-side__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75"><rect x="3" y="6" width="18" height="12" rx="2" /><path d="M3 10h18M7 14h4" /></svg>;
    case "rupee":
      return (
        <svg className="role-side__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
          <path d="M6 5h12M6 9h12M10 5c3 0 5 2 5 4.5S13 14 10 14H8l6 5M8 9v5" />
        </svg>
      );
    case "match":
      return <svg className="role-side__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75"><circle cx="9" cy="12" r="6" /><circle cx="15" cy="12" r="6" /></svg>;
    case "settings":
      return <svg className="role-side__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75"><circle cx="12" cy="12" r="3" /><path d="M12 3v2M12 19v2M3 12h2M19 12h2M5.6 5.6l1.4 1.4M17 17l1.4 1.4M18.4 5.6L17 7M7 17l-1.4 1.4" /></svg>;
    case "logout":
      return <svg className="role-side__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75"><path d="M9 4H6a2 2 0 00-2 2v12a2 2 0 002 2h3" /><path d="M16 17l5-5-5-5" /><path d="M21 12H9" /></svg>;
    default:
      return null;
  }
}

function authRoleFromLayoutRole(role) {
  if (role === "customer") return "CUSTOMER";
  if (role === "caregiver") return "CAREGIVER";
  if (role === "care-manager") return "CARE_MANAGER";
  return "OPS";
}

function RoleSidebar({
  role,
  isOpen = false,
  isExpanded = false,
  onClose = () => {},
  onToggleExpand = () => {},
}) {
  const { isFeatureEnabled, isModuleEnabled } = usePlatformConfig();
  const { canAccessModule } = useAdminPermissions();
  const { onboarding: careManagerOnboarding, loading: careManagerOnboardingLoading } =
    useCareManagerOnboardingStatus({ enabled: role === "careManager" });
  const cfg = ROLE_SIDEBAR_CONFIG[role] || ROLE_SIDEBAR_CONFIG.ops;
  const [isPhysioCaregiver, setIsPhysioCaregiver] = useState(false);
  const [isNurseCaregiver, setIsNurseCaregiver] = useState(false);
  const [physioBadges, setPhysioBadges] = useState({ completionPercent: null, credits: null, approved: false });
  const navItems = useMemo(() => {
    let items = cfg.items;
    if (role === "careManager") {
      items = CARE_MANAGER_SIDEBAR_ITEMS;
      if (!careManagerOnboardingLoading) {
        const unlocked = isCareManagerWorkspaceUnlocked(careManagerOnboarding);
        items = filterCareManagerSidebarItems(items, unlocked);
      }
    }
    if (role === "caregiver" && isPhysioCaregiver) items = PHYSIO_SIDEBAR_ITEMS;
    else if (role === "caregiver" && isNurseCaregiver) items = NURSE_SIDEBAR_ITEMS;
    const layoutRole = role === "customer" ? "customer" : role === "caregiver" ? "caregiver" : role === "careManager" ? "careManager" : "admin";
    return filterNavItemsByFeatures(items, isFeatureEnabled, {
      isModuleEnabled,
      role: layoutRole,
    }).filter((item) =>
      role === "ops" && item.permission ? canAccessModule(item.permission) : true
    );
  }, [
    cfg.items,
    isFeatureEnabled,
    isModuleEnabled,
    role,
    canAccessModule,
    isPhysioCaregiver,
    isNurseCaregiver,
    careManagerOnboarding,
    careManagerOnboardingLoading,
  ]);
  const profilePath = getProfilePathForRole(authRoleFromLayoutRole(role));
  const [storedCustomerProfile, setStoredCustomerProfile] = useState(() =>
    role === "customer" ? api.getStoredCustomerProfile() : null
  );
  const [caregiverProfileMeta, setCaregiverProfileMeta] = useState(() =>
    role === "caregiver" ? api.readCaregiverSidebarUser?.() : null
  );
  const [sidebarUserTick, setSidebarUserTick] = useState(0);
  const [tooltip, setTooltip] = useState(null);

  useEffect(() => {
    if (role !== "customer") return undefined;
    setStoredCustomerProfile(api.getStoredCustomerProfile());

    function handleCustomerProfileUpdate(event) {
      setStoredCustomerProfile(event?.detail || api.getStoredCustomerProfile());
    }

    window.addEventListener(api.customerProfileUpdatedEvent, handleCustomerProfileUpdate);
    return () => window.removeEventListener(api.customerProfileUpdatedEvent, handleCustomerProfileUpdate);
  }, [role]);

  useEffect(() => {
    if (role !== "caregiver") {
      setCaregiverProfileMeta(null);
      return undefined;
    }

    let cancelled = false;

    function applyCaregiverDisplay(display, extras = {}) {
      if (!display?.name) return;
      setCaregiverProfileMeta((prev) => ({
        name: display.name,
        initials: display.initials || prev?.initials || cfg.user.initials,
        role: extras.role || prev?.role || cfg.user.role,
        profileImageUrl:
          extras.profileImageUrl || display.profileImageUrl || prev?.profileImageUrl || "",
      }));
      setSidebarUserTick((t) => t + 1);
    }

    async function loadCaregiverSidebarUser() {
      const cached = api.readCaregiverSidebarUser?.();
      if (cached?.name) {
        applyCaregiverDisplay(cached);
      }

      try {
        const statusRes = await api.getCaregiverOnboardingStatus();
        if (cancelled) return;
        const fromOnboarding = api.caregiverDisplayFromOnboarding(statusRes?.onboarding);
        if (fromOnboarding?.name) {
          applyCaregiverDisplay(fromOnboarding);
          api.persistCaregiverSidebarUser?.(fromOnboarding);
          ROLE_SIDEBAR_CONFIG.caregiver.user = {
            initials: fromOnboarding.initials,
            name: fromOnboarding.name,
            role: ROLE_SIDEBAR_CONFIG.caregiver.user.role,
          };
        }
      } catch {
        // keep cached / config fallback
      }

      try {
        const res = await api.getCaregiverProfile();
        if (cancelled) return;
        const level = String(res?.level || "").trim();
        const imageUrl =
          res?.profileImageUrl ||
          res?.profileImageFileUrl ||
          res?.profile_image_file_url ||
          "";
        if (isPhysioServiceRole(res?.serviceRole || res?.service_role)) {
          setIsPhysioCaregiver(true);
        } else if (isNurseServiceRole(res?.serviceRole || res?.service_role)) {
          setIsNurseCaregiver(true);
        }
        setCaregiverProfileMeta((prev) => ({
          ...(prev || {}),
          name: prev?.name || cfg.user.name,
          initials: prev?.initials || cfg.user.initials,
          role: formatCaregiverLevel(level) || prev?.role || cfg.user.role,
          profileImageUrl: imageUrl || prev?.profileImageUrl || "",
        }));
        setSidebarUserTick((t) => t + 1);
      } catch {
        if (!cancelled && !cached?.name) {
          setCaregiverProfileMeta(null);
        }
      }

      try {
        const full = await api.getCompleteCaregiverProfile();
        if (cancelled) return;
        const data = full?.profile || full;
        if (isPhysioFromProfile(data)) {
          setIsPhysioCaregiver(true);
          const dossier = mergePhysioDossier(data?.profileExtended?.physioDossier);
          const pct = calculatePhysioDossierPercent(dossier, data?.basic || {}, data);
          let creditsLabel = null;
          try {
            const sub = await api.fetchTherapistSubscription?.();
            const avail = sub?.credits?.availableCredits;
            if (avail != null) creditsLabel = `${avail} cr`;
          } catch {
            /* optional */
          }
          const approved = Boolean(data?.verification?.adminApproved || data?.verified);
          setPhysioBadges({
            completionPercent: `${pct}%`,
            credits: creditsLabel,
            approved,
          });
          setCaregiverProfileMeta((prev) => ({
            ...(prev || {}),
            role: approved ? "APPROVED" : prev?.role || "Physiotherapist",
          }));
        } else if (isNurseFromProfile(data)) {
          setIsNurseCaregiver(true);
          setCaregiverProfileMeta((prev) => ({
            ...(prev || {}),
            role: prev?.role || "Nurse",
          }));
        }
      } catch {
        /* optional physio enrichment */
      }
    }

    loadCaregiverSidebarUser();

    async function refreshCaregiverExtras() {
      try {
        const res = await api.getCaregiverProfile();
        if (cancelled) return;
        const level = String(res?.level || "").trim();
        const imageUrl =
          res?.profileImageUrl ||
          res?.profileImageFileUrl ||
          res?.profile_image_file_url ||
          "";
        setCaregiverProfileMeta((prev) =>
          prev
            ? {
                ...prev,
                role: formatCaregiverLevel(level) || prev.role,
                profileImageUrl: imageUrl || prev.profileImageUrl,
              }
            : prev
        );
        setSidebarUserTick((t) => t + 1);
      } catch {
        // keep current display name
      }
    }

    function handleCaregiverProfileUpdate(event) {
      const display = api.caregiverDisplayFromOnboarding?.(event?.detail);
      if (display?.name) {
        api.persistCaregiverSidebarUser?.(display);
        ROLE_SIDEBAR_CONFIG.caregiver.user = {
          initials: display.initials,
          name: display.name,
          role: ROLE_SIDEBAR_CONFIG.caregiver.user.role,
        };
        applyCaregiverDisplay(display);
        void refreshCaregiverExtras();
        return;
      }
      loadCaregiverSidebarUser();
    }

    window.addEventListener(api.caregiverProfileUpdatedEvent, handleCaregiverProfileUpdate);
    return () => {
      cancelled = true;
      window.removeEventListener(api.caregiverProfileUpdatedEvent, handleCaregiverProfileUpdate);
    };
  }, [cfg.user.initials, cfg.user.name, cfg.user.role, role]);

  const userMeta = useMemo(() => {
    if (role === "caregiver") {
      const fullName = String(caregiverProfileMeta?.name || cfg.user.name || "").trim();
      const initials =
        caregiverProfileMeta?.initials ||
        (fullName
          ? fullName
              .split(/\s+/)
              .slice(0, 2)
              .map((part) => part.charAt(0).toUpperCase())
              .join("")
          : cfg.user.initials);
      return {
        ...cfg.user,
        name: fullName || cfg.user.name,
        role: caregiverProfileMeta?.role || cfg.user.role,
        initials: initials || cfg.user.initials,
        imageUrl: caregiverProfileMeta?.profileImageUrl || "",
      };
    }

    if (role !== "customer") return cfg.user;
    const firstName = String(
      storedCustomerProfile?.firstName ||
      storedCustomerProfile?.first_name ||
      ""
    ).trim();
    const lastName = String(
      storedCustomerProfile?.lastName ||
      storedCustomerProfile?.last_name ||
      ""
    ).trim();
    const username = String(storedCustomerProfile?.username || storedCustomerProfile?.mobile || "").trim();
    const nameFromParts = `${firstName} ${lastName}`.trim();
    const fullName = String(
      nameFromParts ||
      storedCustomerProfile?.name ||
      storedCustomerProfile?.fullName ||
      username ||
      cfg.user.name ||
      ""
    ).trim();
    const initials = firstName
      ? lastName
        ? `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase()
        : firstName.charAt(0).toUpperCase()
      : fullName
        ? fullName
            .split(/\s+/)
            .slice(0, 2)
            .map((part) => part.charAt(0).toUpperCase())
            .join("")
        : (cfg.user.initials || "C");
    return {
      ...cfg.user,
      name: fullName || cfg.user.name,
      initials: initials || cfg.user.initials,
      imageUrl:
        storedCustomerProfile?.profileImageUrl ||
        storedCustomerProfile?.profile_image_url ||
        storedCustomerProfile?.avatarUrl ||
        storedCustomerProfile?.photoUrl ||
        "",
    };
  }, [caregiverProfileMeta, cfg.user, role, sidebarUserTick, storedCustomerProfile]);
  const resolvedUserImageUrl = useProtectedFileUrl(userMeta.imageUrl);

  function showTooltip(event, text) {
    if (isExpanded) return;
    const rect = event.currentTarget.getBoundingClientRect();
    setTooltip({
      text,
      left: rect.right + 10,
      top: rect.top + rect.height / 2,
    });
  }

  function hideTooltip() {
    setTooltip(null);
  }

  return (
    <aside
      className={`role-side ${isOpen ? "role-side--open" : "role-side--closed"} ${isExpanded ? "role-side--expanded" : "role-side--compact"}`}
      aria-label={cfg.ariaLabel}
    >
      <div className="role-side__deco-dots" aria-hidden="true" />
      <div className="role-side__top">
        <button
          type="button"
          className="role-side__toggle role-side__tooltip-target"
          aria-label={isExpanded ? "Collapse sidebar" : "Expand sidebar"}
          data-tooltip={isExpanded ? "Collapse menu" : "Expand menu"}
          onClick={onToggleExpand}
          onMouseEnter={(e) => showTooltip(e, isExpanded ? "Collapse menu" : "Expand menu")}
          onMouseLeave={hideTooltip}
          onFocus={(e) => showTooltip(e, isExpanded ? "Collapse menu" : "Expand menu")}
          onBlur={hideTooltip}
        >
          ☰
        </button>
        <div className="role-side__brand" aria-label="NEMA Care Hub">
          <img src={nemaCareLogo} alt="NEMA Care Hub" className="role-side__logo-img" />
        </div>
      </div>

      <nav className="role-side__nav">
        {navItems.map((item) => {
          const badge =
            item.badgeKey === "completionPercent"
              ? physioBadges.completionPercent
              : item.badgeKey === "credits"
                ? physioBadges.credits
                : null;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={onClose}
              title={item.label}
              data-tooltip={item.label}
              onMouseEnter={(e) => showTooltip(e, item.label)}
              onMouseLeave={hideTooltip}
              onFocus={(e) => showTooltip(e, item.label)}
              onBlur={hideTooltip}
              className={({ isActive }) =>
                `role-side__link role-side__tooltip-target ${isActive ? "role-side__link--active" : ""}`
              }
            >
              <Icon name={item.icon} />
              <span className="role-side__label">{item.label}</span>
              {badge ? <span className="role-side__badge">{badge}</span> : null}
            </NavLink>
          );
        })}
      </nav>

      <div className="role-side__footer">
        <HeaderProfileMenu
          className="role-side__profile-menu"
          triggerClassName="role-side__profile-trigger"
          displayName={userMeta.name}
          roleLabel={userMeta.role}
          initials={userMeta.initials}
          avatarUrl={resolvedUserImageUrl}
          profilePath={profilePath}
          authRole={authRoleFromLayoutRole(role)}
          compactName
          showCaret={isExpanded}
        />
      </div>
      {tooltip && !isExpanded ? (
        <div className="role-side__floating-tooltip" style={{ left: tooltip.left, top: tooltip.top }}>
          {tooltip.text}
        </div>
      ) : null}
    </aside>
  );
}

export default RoleSidebar;
